import { ENDPOINTS, getFullUrl } from "./config";

export interface ClinicDetails {
  id: string;
  name: string;
  mobile_number?: string;
  address?: string;
}

export interface ProductEnquiryItem {
  id: number;
  clinic_id: string;
  enquiry_item: string;
  patient_name?: string;
  patient_mobile_number?: string;
  created_at: string;
  clinic_details?: ClinicDetails;
}

export interface ProductEnquiryAnalyticsData {
  totalEnquiries: number;
  todayEnquiries: number;
  enquiriesByClinic: {
    clinic_id: string;
    count: number;
    clinic_details?: ClinicDetails;
  }[];
  topEnquiryItems: {
    enquiry_item: string;
    count: number;
    percentage: number;
  }[];
  patientProvidedCount: number;
  anonymousCount: number;
}

export class ProductEnquiryService {
  private static getToken(): string | null {
    return localStorage.getItem("token");
  }

  static async createEnquiry(payload: {
    clinic_id: string;
    enquiry_item: string;
    patient_name?: string;
    patient_mobile_number?: string;
  }) {
    try {
      const response = await fetch(getFullUrl(ENDPOINTS.PRODUCT_ENQUIRY.CREATE), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken() || ""}`,
        },
        body: JSON.stringify(payload),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to create product enquiry",
      };
    }
  }

  static async getEnquiries(params: {
    page?: number;
    limit?: number;
    pageSize?: number;
    clinic_id?: string;
    clinicId?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  } = {}) {
    try {
      const url = new URL(getFullUrl(ENDPOINTS.PRODUCT_ENQUIRY.LIST));
      if (params.page) url.searchParams.append("page", params.page.toString());
      const limitVal = params.limit || params.pageSize;
      if (limitVal) url.searchParams.append("limit", limitVal.toString());
      const clinicVal = params.clinic_id || params.clinicId;
      if (clinicVal) url.searchParams.append("clinic_id", clinicVal);
      if (params.search) url.searchParams.append("search", params.search);
      if (params.startDate) url.searchParams.append("startDate", params.startDate);
      if (params.endDate) url.searchParams.append("endDate", params.endDate);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken() || ""}`,
        },
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch product enquiries",
      };
    }
  }

  static async deleteEnquiry(id: number | string) {
    try {
      const endpoint = ENDPOINTS.PRODUCT_ENQUIRY.DELETE.replace(":id", String(id));
      const response = await fetch(getFullUrl(endpoint), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${this.getToken() || ""}`,
        },
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to delete product enquiry",
      };
    }
  }

  static async getAnalytics(params: {
    clinic_id?: string;
    clinicId?: string;
    startDate?: string;
    endDate?: string;
  } = {}) {
    try {
      const url = new URL(getFullUrl(ENDPOINTS.PRODUCT_ENQUIRY.ANALYTICS));
      const clinicVal = params.clinic_id || params.clinicId;
      if (clinicVal) url.searchParams.append("clinic_id", clinicVal);
      if (params.startDate) url.searchParams.append("startDate", params.startDate);
      if (params.endDate) url.searchParams.append("endDate", params.endDate);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken() || ""}`,
        },
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch product enquiry analytics",
      };
    }
  }
}
